import React from 'react';
import {Linking, Platform, StyleSheet, Pressable, View} from 'react-native';
import Styles from '../../Styles';
import Localized from 'src/constants/AppStrings';
import AVText from 'src/components/elements/AVText';
import LocationPermissionErrorIcon from 'src/components/img/svg/LocationPermissionErrorIcon';
import LocationMarkerErrorIcon from 'src/components/img/svg/LocationMarkerErrorIcon';
import Settings from 'src/Settings';
import {getDescriber} from 'src/components/elements/home/descriptors/campusComponents/DescriptorType';
import {getLayoutDescriber} from 'src/components/elements/layoutDescriptor/DescriptorType';
import GlobeMarker from 'src/components/img/svg/GlobeMarker';
import {getButtonDescriber} from 'src/components/elements/buttonDescriptor/DescriptorType';
import NewLocationMarker from 'src/components/img/svg/NewLocationMarker';

//Location Error Types
//LocationPermission - When Location Permission is disbaled/denied
//NoNearbyLocations - When Location Permission enabled but there are no nearby location
//LocationBalanceMismatch - When Location Balance and Consumer Balance Mismatched
export enum LocationErrorStatusType {
  LocationPermission = 'LocationPermission',
  NoNearbyLocations = 'NoNearbyLocations',
  LocationBalanceMismatch = 'LocationBalanceMismatch',
}

type LocationErrorStatusProps = {
  locationErrorStatusType: LocationErrorStatusType;
};
const {getCampusLocationStyles} = getDescriber();
const buttonColor = getButtonDescriber().checkRoundedBtnColor();

const LocationErrorStatus = (props: LocationErrorStatusProps) => {
  const descriptionText = (): string => {
    switch (props.locationErrorStatusType) {
      case LocationErrorStatusType.LocationPermission:
        return Localized.Labels
          .turn_location_services_on_to_access_nearby_locations;
      case LocationErrorStatusType.NoNearbyLocations:
        return Localized.Labels.there_are_no_locations_near_you;
      case LocationErrorStatusType.LocationBalanceMismatch:
        return Localized.Labels
          .your_account_balance_is_not_valid_at_some_locations;
    }
  };

  const locationMarkerProps = {
    size: 75,
    primaryFill: Styles.Colors.Aramark.primary,
    secondaryFill: Styles.Colors.Canteen.lightGray,
  };

  const isAramark = Settings.isAramark();

  return (
    <View
      style={[
        styles.container,
        props.locationErrorStatusType ===
        LocationErrorStatusType.LocationPermission
          ? {height: Styles.Heights.h7 * 2.6}
          : {height: Styles.Heights.h7 * 2},
      ]}
    >
      <View style={styles.imageView}>
        {props.locationErrorStatusType ===
        LocationErrorStatusType.LocationPermission ? (
          isAramark ? (
            <GlobeMarker {...locationMarkerProps} />
          ) : (
            <LocationPermissionErrorIcon />
          )
        ) : isAramark ? (
          <NewLocationMarker {...locationMarkerProps} />
        ) : (
          <LocationMarkerErrorIcon />
        )}
      </View>
      <AVText
        style={[
          getCampusLocationStyles().descriptionText,
          styles.descriptionText,
        ]}
        accessible={false}
        accessibilityElementsHidden={true}
        accessibilityLabel={descriptionText()}
        accessibilityRole="text"
        aria-label={`${descriptionText()}, text`}
        maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm1}
      >
        {descriptionText()}
      </AVText>
      {props.locationErrorStatusType ===
        LocationErrorStatusType.LocationPermission &&
        (Settings.is365Pay() || Settings.isAramark()) && (
          <View style={{paddingTop: 12}}>
            <Pressable
              style={[styles.locationSettings]}
              onPress={() => {
                Linking.openSettings();
              }}
              accessibilityRole="button"
              accessibilityLabel={Localized.Buttons.go_to_location}
              nativeID={Localized.Buttons.go_to_location}
              role="button"
              aria-label={Localized.Buttons.go_to_location}
              testID={Localized.Buttons.go_to_location}
            >
              <AVText
                style={styles.locationSettingsbtnStyle}
                maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm6}
                testID={Localized.Buttons.go_to_location}
                adjustsFontSizeToFit={true}
              >
                {Localized.Buttons.go_to_location}
              </AVText>
            </Pressable>
          </View>
        )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    //height: Styles.Heights.h7 * 2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  descriptionText: {
    color: Settings.is365Pay() ? '#111' : Styles.black,
    fontSize: getLayoutDescriber().bodyLargeFontSize(),
    fontWeight: '400',
    textAlign: 'center',
    marginTop: Styles.Spacing.m2,
    marginHorizontal: Styles.Spacing.m2,
  },
  imageView: {
    justifyContent: 'center',
    alignItems: 'center',
    width: Styles.Heights.h5,
    height: Styles.Heights.h5,
    marginBottom: Styles.Spacing.m2,
  },
  locationSettings: {
    borderTopColor: buttonColor,
    borderBottomColor: buttonColor,
    borderLeftColor: buttonColor,
    borderRightColor: buttonColor,
    borderRadius: Styles.Spacing.m4,
    borderWidth: Platform.OS === 'android' ? 1.2 : 1,
    paddingVertical: Styles.Spacing.m2 + 2,
    paddingHorizontal: Styles.Spacing.m3 + Styles.Spacing.m1,
    overflow: 'hidden',
    backgroundColor: buttonColor,
    minHeight: Styles.MinTouchableArea,
    minWidth: Styles.MinTouchableArea,
  },
  locationSettingsbtnStyle: {
    fontWeight: Platform.OS === 'android' ? 'bold' : '700',
    fontFamily: getButtonDescriber().buttonTextFontFamily(),
    fontSize: getLayoutDescriber().bodyFontSize(),
    color: Styles.white,
    textTransform: 'capitalize',
  },
});

export default LocationErrorStatus;
